import React from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Select, ShowWhen } from "@/components/common";
import SelectNationalDeliveryPayTypeFlow from "@/components/common/select/national-delivery-pay-type-select/select-national-delivery-pay-type-flow";
import { CloseModalIcon, EditIcon } from "@/components/assets/icons";

import { apiRequest } from "@/utils/apiRequest";
import {
  EDIT_PREDELIVERY_PAYMENT,
  GET_SHIPMENT_LIST,
  VIEW_PARCEL,
} from "@/constants/Query";

type TProps = {
  startEditing: () => void;
  closeEditing: () => void;
  isEditing: boolean;
  isEditable: boolean;
  paymentText: string;
  shipmentId: number;
  payTypeId: number;
};

const PredeliveryPayType = ({
  startEditing,
  closeEditing,
  isEditing,
  isEditable,
  paymentText,
  payTypeId,
  shipmentId,
}: TProps) => {
  const queryClient = useQueryClient();
  const t = useTranslations();

  const { values, setFieldValue, resetForm, handleSubmit } = useFormik({
    initialValues: {
      payTypeId: { id: payTypeId, mainText: paymentText },
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await changePreDeliveryAddress({
          shipmentId,
          payTypeId: values.payTypeId?.id,
        });
        closeEditing();
        queryClient.invalidateQueries({ queryKey: [GET_SHIPMENT_LIST] });
        queryClient.invalidateQueries({ queryKey: [VIEW_PARCEL] });
        toast.success("Success", {
          position: "top-center",
        });
      } catch (error) {
        closeEditing();
        toast.error("Error occured", {
          position: "top-center",
        });
      }
    },
  });

  const { mutateAsync: changePreDeliveryAddress } = useMutation({
    mutationFn: (data: { shipmentId: number; payTypeId: number }) =>
      apiRequest(EDIT_PREDELIVERY_PAYMENT, "put", data),
  });

  return (
    <>
      <ShowWhen condition={!isEditing}>
        <div className="flex items-center gap-2">
          <p className="text-black">{paymentText ? t(paymentText) : ""}</p>
          <ShowWhen condition={isEditable}>
            <span className="primary-bg cursor-pointer rounded-full p-1">
              <EditIcon onClick={startEditing} className="fill-white" />
            </span>
          </ShowWhen>
        </div>
      </ShowWhen>
      <ShowWhen condition={isEditing}>
        <div className="flex w-full items-center justify-end gap-4">
          <Select
            label="payment_method"
            containerClassName="!max-w-[300px]"
            translation
            selectedValue={values.payTypeId}
            name="payTypeId"
          >
            <SelectNationalDeliveryPayTypeFlow
              onSelect={(selectedOption) => {
                setFieldValue("payTypeId", selectedOption);
                handleSubmit();
              }}
            />
          </Select>
          <span className="primary-bg p-1f h-8 w-8 cursor-pointer rounded-full">
            <CloseModalIcon
              onClick={() => {
                closeEditing();
                resetForm();
              }}
              className="fill-white"
            />
          </span>
        </div>
      </ShowWhen>
    </>
  );
};

export default PredeliveryPayType;
